// TagCloud — 사이드바 S4 섹션 (U39)
// 모든 태그를 item_count 기반 5단계 폰트 크기로 표시하는 태그 클라우드.
//
// 동작:
// - useTags hook으로 GET /api/tags (빈도순 최대 20개) 수신
// - item_count로 5분위 계산 → text-xs / text-xs / text-sm / text-base / text-base font-semibold
// - 태그 칩 클릭 → URL ?tag={name} 갱신 (기존 다른 필터는 유지)
// - 현재 ?tag 파라미터와 일치하는 태그 강조 (active 클래스)
// - 20개 초과 시 "더보기" 링크 → /tags 페이지로 이동
// - 빈 상태: "태그가 아직 없어요" 작은 텍스트
//
// 의존: lucide-react (U34), useTags hook, TagPill 색상 로직 참조

import { useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useTags } from '../../hooks/useTags';
import type { TagAutocomplete } from '../../api/tags';

// ─── 5분위 폰트 매핑 ────────────────────────────────────

/**
 * TagCloud 아이템에 적용할 폰트 크기 CSS 클래스.
 * q1·q2 = text-xs, q3 = text-sm, q4 = text-base, q5 = text-base + font-semibold
 */
export type FontTier = 'q1' | 'q2' | 'q3' | 'q4' | 'q5';

/** FontTier → CSS 클래스 이름 */
const FONT_CLASS: Record<FontTier, string> = {
  q1: 'tag-cloud__chip--q1', // text-xs
  q2: 'tag-cloud__chip--q2', // text-xs
  q3: 'tag-cloud__chip--q3', // text-sm
  q4: 'tag-cloud__chip--q4', // text-base
  q5: 'tag-cloud__chip--q5', // text-base + font-semibold
};

/**
 * item_count 배열을 받아 각 값에 FontTier를 할당한다.
 * 5분위(20% 구간)로 나누어 tier를 결정한다.
 * 모든 count가 동일하면 q3(중간)으로 단일화.
 *
 * @param tags - item_count가 포함된 태그 배열
 * @returns 각 태그의 FontTier 배열 (태그 배열과 동일 인덱스)
 */
export function computeFontTiers(tags: Pick<TagAutocomplete, 'item_count'>[]): FontTier[] {
  if (tags.length === 0) return [];

  const counts = tags.map((t) => t.item_count);
  const min = Math.min(...counts);
  const max = Math.max(...counts);

  // 모든 값이 동일하면 중간 tier 단일화
  if (min === max) {
    return tags.map(() => 'q3');
  }

  const range = max - min;

  return counts.map((c): FontTier => {
    // 정규화 0~1
    const norm = (c - min) / range;
    // 5구간 분할 [0, 0.2), [0.2, 0.4), [0.4, 0.6), [0.6, 0.8), [0.8, 1.0]
    if (norm < 0.2) return 'q1';
    if (norm < 0.4) return 'q2';
    if (norm < 0.6) return 'q3';
    if (norm < 0.8) return 'q4';
    return 'q5';
  });
}

// ─── 색상 폴백 팔레트 ─────────────────────────────────

const FALLBACK_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444',
  '#f97316', '#eab308', '#22c55e', '#14b8a6',
  '#3b82f6', '#06b6d4',
];

/** 태그 이름을 팔레트 인덱스로 해시 — TagPill과 동일 로직 */
function hashColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  }
  return FALLBACK_COLORS[h % FALLBACK_COLORS.length];
}

// ─── TagCloudChip ────────────────────────────────────

interface TagCloudChipProps {
  tag: TagAutocomplete;
  tier: FontTier;
  isActive: boolean;
  onClick: (tag: TagAutocomplete) => void;
}

/** 태그 클라우드 개별 칩. 색상 점 + 이름, 클릭 시 ?tag 갱신. */
function TagCloudChip({ tag, tier, isActive, onClick }: TagCloudChipProps) {
  const dotColor = tag.color ?? hashColor(tag.name);

  const handleClick = () => onClick(tag);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(tag);
    }
  };

  return (
    <span
      className={`tag-cloud__chip ${FONT_CLASS[tier]}${isActive ? ' tag-cloud__chip--active' : ''}`}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-pressed={isActive}
      aria-label={`태그 필터: ${tag.name} (${tag.item_count}개)`}
      title={`${tag.name} (${tag.item_count}개)`}
    >
      {/* 색상 점 */}
      <span
        className="tag-cloud__dot"
        style={{ backgroundColor: dotColor }}
        aria-hidden="true"
      />
      {/* 태그 이름 */}
      <span className="tag-cloud__name">{tag.name}</span>
    </span>
  );
}

// ─── TagCloud ────────────────────────────────────────

/** 사이드바 태그 클라우드 섹션 최대 표시 수 */
const MAX_DISPLAY = 20;

/**
 * TagCloud — 사이드바 S4 섹션.
 * 빈도 기반 5단계 폰트 크기로 태그를 구름 형태로 표시.
 */
export function TagCloud() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTag = searchParams.get('tag');

  // 빈도순 태그 목록 (limit=20 고정 — 더보기는 /tags 페이지로)
  const { data: tags = [], isLoading } = useTags('', MAX_DISPLAY);

  // 폰트 tier 계산
  const tiers = computeFontTiers(tags);

  /**
   * 태그 칩 클릭 핸들러.
   * 이미 활성 태그 재클릭 → 필터 해제 (?tag 제거).
   * 다른 태그 클릭 → ?tag={name} 설정.
   */
  const handleTagClick = (tag: TagAutocomplete) => {
    const next = new URLSearchParams(searchParams);
    if (activeTag === tag.name) {
      next.delete('tag');
    } else {
      next.set('tag', tag.name);
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <section className="sidebar__section" aria-label="태그 클라우드">
      <div className="sidebar__heading">
        <span>태그</span>
      </div>

      {/* 로딩 상태 */}
      {isLoading && (
        <div className="muted sidebar__hint">불러오는 중…</div>
      )}

      {/* 빈 상태 */}
      {!isLoading && tags.length === 0 && (
        <div className="muted sidebar__hint tag-cloud__empty">
          항목을 만들 때 태그를 입력하면 여기 모입니다
        </div>
      )}

      {/* 태그 클라우드 */}
      {!isLoading && tags.length > 0 && (
        <>
          <div className="tag-cloud" role="list" aria-label="태그 목록">
            {tags.map((tag, idx) => (
              <span key={tag.id} role="listitem">
                <TagCloudChip
                  tag={tag}
                  tier={tiers[idx]}
                  isActive={activeTag === tag.name}
                  onClick={handleTagClick}
                />
              </span>
            ))}
          </div>

          {/* 더보기 — API 반환 건수가 MAX_DISPLAY에 달하면 추가 태그가 있을 수 있음 */}
          {tags.length >= MAX_DISPLAY && (
            <div className="tag-cloud__more">
              <Link to="/tags" className="tag-cloud__more-link">
                더보기 →
              </Link>
            </div>
          )}
        </>
      )}
    </section>
  );
}
